/*
 * Vital Pantry - free delivery progress
 *
 * Tells the customer how close the basket is to free delivery, in the cart
 * drawer and on the cart page. Delivery pricing used to appear for the first
 * time at the checkout, which is where 44% of carts were being abandoned.
 *
 * The threshold is a shop metafield (vitalpantry.free_delivery_threshold) so it
 * can follow whatever promotion is running - a free-shipping discount can lower
 * it for a while, and a bar that quotes the wrong number is worse than no bar
 * at all.
 *
 * ONLY THE MELBOURNE DELIVERY ZONE SEES IT. Free delivery over the threshold
 * is a local delivery rule covering 130 metro postcodes. Regional Victoria and
 * interstate pay a flat rate however big the order is, and the ten postcodes
 * within 5km are free from the first dollar. For all of them a bar counting up
 * to "free delivery" is a promise the checkout breaks, so they do not get one.
 * The qualifying list is vitalpantry.free_threshold_postcodes.
 *
 * Injected rather than templated, like vp-delivery.js and vp-addon.js: the
 * theme re-renders the cart's own contents wholesale, so this sits just outside
 * them and survives every re-render.
 */
(function () {
  'use strict';

  var cfg = window.VPFreeship;
  if (!cfg || !(cfg.threshold > 0)) return;

  var THRESHOLD = Math.round(cfg.threshold * 100);   // cents, as /cart.js reports
  var total = (cfg.cart && cfg.cart.total) || 0;
  var count = (cfg.cart && cfg.cart.count) || 0;

  // Gift cards are emailed, so a cart holding nothing but gift cards has no
  // delivery to be close to - "You are $79 away from free delivery" under a
  // single emailed card reads like a mistake. Seeded from Liquid
  // (cart.requires_shipping) so the bar does not appear and then remove itself;
  // /cart.js replaces it as soon as it answers.
  var needsShipping = !(cfg.cart && cfg.cart.requiresShipping === false);

  // Postcode gate. Built once into a lookup so render() stays cheap - it runs
  // on every cart mutation. An empty list means nobody qualifies, which hides
  // the bar: if we cannot tell who gets free delivery we say nothing, rather
  // than tell everyone they do.
  var QUALIFYING = (function () {
    var map = {};
    var list = (cfg.postcodes || []);
    for (var i = 0; i < list.length; i++) map[String(list[i]).trim()] = true;
    return map;
  })();

  var postcode = String(cfg.postcode || '').trim();

  function qualifies() {
    return QUALIFYING[postcode] === true;
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // "$9.50" / "$12" - whole dollars stay whole, which reads better in a nudge.
  function money(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  function build(where) {
    var box = document.createElement('div');
    box.className = 'vpf vpf--' + where;
    box.setAttribute('data-vpf', where);
    box.hidden = true;
    box.innerHTML =
      '<p class="vpf__msg" data-vpf-msg></p>' +
      '<div class="vpf__track"><div class="vpf__fill" data-vpf-fill></div></div>';
    return box;
  }

  function inject() {
    var drawer = document.querySelector('#cart-notification .cart_notification_links_inner');
    if (drawer && !drawer.querySelector('[data-vpf]')) {
      drawer.insertBefore(build('drawer'), drawer.firstChild);
    }

    var blocks = document.querySelector('#main-cart-footer .cart__blocks');
    if (blocks && !blocks.parentNode.querySelector('[data-vpf]')) {
      blocks.parentNode.insertBefore(build('page'), blocks);
    }
  }

  function render() {
    var remaining = THRESHOLD - total;
    var pct = THRESHOLD > 0 ? Math.min(100, Math.round((total / THRESHOLD) * 100)) : 0;
    var done = remaining <= 0;
    var eligible = qualifies();

    document.querySelectorAll('[data-vpf]').forEach(function (box) {
      // An empty cart, a cart of nothing but emailed gift cards, or a customer
      // whose postcode does not get free delivery, has nothing to be close to.
      box.hidden = count === 0 || !needsShipping || !eligible;
      box.classList.toggle('is-done', done);

      var msg = box.querySelector('[data-vpf-msg]');
      if (msg) {
        msg.textContent = done
          ? 'Your delivery is free.'
          : 'You are ' + money(remaining) + ' away from free delivery.';
      }

      var fill = box.querySelector('[data-vpf-fill]');
      if (fill) fill.style.width = pct + '%';
    });
  }

  function refresh() {
    return fetch(rootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        total = c.total_price || 0;
        count = c.item_count || 0;
        needsShipping = (c.items || []).some(function (it) { return it.requires_shipping !== false; });
        // The date picker writes the postcode onto the cart, so /cart.js is
        // also how a postcode CHANGE reaches this bar - no page load needed.
        postcode = String((c.attributes && c.attributes['Delivery Postcode']) || '').trim();
        render();
      })
      .catch(function () { /* keep the last known figures */ });
  }

  var timer = null;
  function soon() { clearTimeout(timer); timer = setTimeout(refresh, 150); }

  function init() {
    inject();
    render();

    // Same signal vp-addon.js watches: the theme replaces one of these on every
    // add, remove and quantity change, including adds that publish no event.
    ['cart-notification-product', 'main-cart-items'].forEach(function (elId) {
      var el = document.getElementById(elId);
      if (el && window.MutationObserver) {
        new MutationObserver(soon).observe(el, { childList: true, subtree: true });
      }
    });

    // Opening the drawer from the header icon only slides it in - the cart
    // itself is not re-rendered, so nothing above fires. Without this the bar
    // can show the figures it was printed with rather than the current cart.
    //
    // The date picker's own controls are in here too: picking a postcode or a
    // date writes a cart attribute without touching a line item, so none of the
    // observers above would notice, and the bar is gated on that postcode.
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      if (e.target.closest(
        'open-minicart, [data-vpd-open], [data-vpf], [data-vpd-postcode-submit],' +
        ' [data-vpd-dates] button, [data-vpd-split], [data-vpd-change-postcode]'
      )) soon();
    }, true);

    // The cart page's own quantity handler publishes this one.
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, soon);
    }

    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
